import React from 'react';
import { useField } from 'formik';
import {
  Box,
  Button,
  debounce,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import { cx } from '../../helpers/global';
import ColorPicker from '../global/ColorPicker';
import InputLabel from './InputLabel';

interface Props {
  name: string;
  label: string;
  disabled: boolean;
  isError?: boolean;
  isTouched?: boolean;
  endAdornment?: {
    icon: SvgIconComponent;
    onClick: () => void;
  };
  tooltip?: string;
  colors?: string[];
}

const END_ADORNMENT_WIDTH = 40;

export default function ColorInput({
  name,
  label,
  disabled,
  isError: isErrorProp,
  isTouched,
  endAdornment,
  colors,
  tooltip,
}: Props) {
  const theme = useTheme();
  const [{ value }, { error, touched }, { setTouched, setValue }] =
    useField(name);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isError =
    !anchorEl && (touched || isTouched) && (!!error || isErrorProp);

  const debouncedSetValue = React.useMemo(
    () =>
      debounce((newValue: string) => {
        setValue(newValue);
      }, 50),
    [setValue],
  );

  const onInput = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSetValue(e.target.value);
    },
    [debouncedSetValue],
  );

  const onOpenColorPicker = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (colors?.length) {
        setAnchorEl(e.currentTarget);
      } else {
        inputRef.current?.click();
      }
      setTouched(true);
    },
    [colors?.length, setTouched],
  );

  const EndAdornmentIcon = endAdornment?.icon;

  return (
    <>
      <div className={cx('input', isError && 'error')}>
        <InputLabel name={name} label={label} tooltip={tooltip} />
        <Box position="relative" flex={1} display="flex" alignItems="center">
          <Stack
            flexGrow={1}
            height="50px"
            p={2}
            boxSizing="border-box"
            left="1px"
            top="1px"
            border={`1px solid ${
              isError ? 'red' : theme.palette.lightGrey.main
            }`}
            flexDirection="row"
            alignItems="center">
            <Button
              onClick={onOpenColorPicker}
              variant="text"
              sx={{
                minWidth: 'auto',
                p: 0,
                borderRadius: '100%',
              }}>
              <Stack
                width={30}
                height={30}
                borderRadius="100%"
                border={
                  !value
                    ? `1px solid ${theme.palette.darkerLightGrey.main}`
                    : 'none'
                }
                bgcolor={value}
                sx={{
                  // checkberboard if empty
                  backgroundImage: !value
                    ? `linear-gradient(45deg, ${theme.palette.darkerLightGrey.main} 25%, transparent 25%),
                    linear-gradient(-45deg, ${theme.palette.darkerLightGrey.main} 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, ${theme.palette.darkerLightGrey.main} 75%),
                    linear-gradient(-45deg, transparent 75%, ${theme.palette.darkerLightGrey.main} 75%)`
                    : 'none',
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                }}
              />
            </Button>

            <input
              ref={inputRef}
              type="color"
              id={name}
              name={name}
              disabled={disabled}
              onInput={onInput}
              defaultValue={value}
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                opacity: 0,
                pointerEvents: 'none',
              }}
            />
          </Stack>

          {endAdornment && EndAdornmentIcon && (
            <Box
              p={0.5}
              width={END_ADORNMENT_WIDTH}
              zIndex={6}
              boxSizing="border-box"
              position="absolute"
              right={0}
              top={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center">
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  endAdornment.onClick();
                }}
                sx={{
                  color: theme.palette.darkGrey.main,
                  p: 0.5,
                }}>
                <EndAdornmentIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </div>
      {!!colors?.length && (
        <ColorPicker
          currentColor={value}
          colors={colors}
          onChange={newValue => {
            setValue(newValue);
            setAnchorEl(null);
          }}
          onCustomColor={() => {
            setAnchorEl(null);
            inputRef.current?.click();
          }}
          open={!!anchorEl}
          onClose={() => {
            setAnchorEl(null);
          }}
          anchorEl={anchorEl}
          topOffset={10}
        />
      )}
    </>
  );
}
